import { Fragment, useCallback, useMemo, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Size } from '@Components/designConstants';
import { DivergentPositive400, Warning600 } from '@src/utils/colors';
import { useSetError } from '../../../../redux/modals';
import { exportMultiSheetToExcel } from '../../../../utils/download-utils';
import { BrandIndexGroundTruthPanel } from '../components/BrandIndexGroundTruthPanel';
import { DataQaChart, SeriesConfig } from '../components/DataQaChart';
import { DmaChoropleth } from '../components/DmaChoropleth';
import { GqvGroundTruthPanel } from '../components/GqvGroundTruthPanel';
import { KpiBar, KpiTileData } from '../components/KpiBar';
import { ReviewControls } from '../components/ReviewControls';
import { SectionHeader } from '../components/SectionHeader';
import { ProductType, SectionStatus, SectionStatusType } from '../data-qa-constants';
import {
  ConfigSummary,
  DataQaDataset,
  DependentVarStats,
  MmmConfig,
  SectionDiffs,
  WeekRow
} from '../data-qa-types';
import { filterWeeksToFencepost, modelTypeForProductType } from '../data-qa-utils';
import { buildDependentVarExportSheets } from '../dependent-var-export-utils';
import { computeDivergentRanges } from '../divergence-ranges';
import { useAllModelsExport } from '../hooks/useAllModelsExport';

interface DependentVarSectionProps {
  config: MmmConfig;
  dataset: DataQaDataset;
  diffs: SectionDiffs;
  status: SectionStatusType;
  notes: string;
  pending: boolean;
  isColdStart: boolean;
  onApprove: () => void;
  onFlag: (notes: string) => void;
  onNoteChange: (notes: string) => void;
  // Redshift table slug -- builds the GQV compare-to-canonical SQL block and the
  // export's file/sheet names.
  company: string;
  // Every model config for the company -- the export covers all models, not just the
  // one on screen.
  configs: ConfigSummary[];
  // See ReviewControls' approveBlockedReason.
  approveBlockedReason?: string;
}

const CANONICAL_COLOR = Warning600;

// One color per YouGov BrandIndex metric (see BrandIndexCard) -- paired solid/dashed
// (MMM/canonical) rather than the single shared CANONICAL_COLOR above, since with 4
// metrics on one chart a dashed line needs to visually pair with its OWN metric's solid
// line to be legible, not just read as "the" canonical series. Same "Streamlit series
// colors" style carried over elsewhere on this page (PortfolioSection's SPEND_SERIES/
// IMPRESSIONS_SERIES) rather than the design-system palette -- a categorical chart
// palette with no existing equivalent there either.
const BRAND_INDEX_COLORS: Record<string, string> = {
  yougov__awareness: '#2ca02c',
  yougov__consideration: '#1f77b4',
  yougov__buzz: '#9467bd',
  yougov__purchase_intent: '#d62728'
};
const DEFAULT_BRAND_INDEX_COLOR = '#6b7280';

const money0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
// Two decimals for non-currency targets (Brand Equity's GQV index) -- a plain 0-digit
// format read "1"/"0"/"2" for values that are actually meaningfully more precise than
// that, e.g. an Avg Weekly of 0.87 rounding away its only informative digits.
const number2 = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const TIPS = (
  <>
    <li>
      <strong>Trend consistency</strong>: The target variable should show a plausible trend
      (seasonal patterns, growth, etc.)
    </li>
    <li>
      <strong>Zero or negative values</strong>: These may indicate data collection issues
    </li>
    <li>
      <strong>Magnitude</strong>: Verify the scale matches your expectations (e.g., weekly revenue
      in expected range)
    </li>
    <li>
      <strong>Flag</strong> if the target variable has unexplained anomalies or looks unreliable
    </li>
  </>
);

interface DepTargetCardProps {
  stat: DependentVarStats;
  weeks: WeekRow[];
  bookmarkWeekIso?: string;
  isColdStart: boolean;
  defaultOpen: boolean;
  // Canonical comparison applies to the primary target (charts w.dependentValue).
  isPrimary: boolean;
  // False for Brand Equity's GQV stage (dependent_var "gqv_combined") -- a search-
  // interest index, not a dollar-denominated metric, so it shouldn't render with a $
  // prefix like every other product type's (and Brand Equity's own second stage's)
  // revenue-based dependent variable does.
  isCurrency: boolean;
  // Redshift table slug -- only needed to build the GQV compare-to-canonical SQL block.
  company: string;
  // dataset.reviewGranularity -- only needed to build the GQV compare-to-canonical SQL
  // block's canonical-side bucketing (see buildGqvGroundTruthSql's dateTruncUnitFor).
  reviewGranularity?: string;
}

const DepTargetCard = ({
  stat,
  weeks,
  bookmarkWeekIso,
  isColdStart,
  defaultOpen,
  isPrimary,
  isCurrency,
  company,
  reviewGranularity
}: DepTargetCardProps) => {
  const [open, setOpen] = useState(defaultOpen);
  // Each target charts under the 'dependentValue' key; brand-health targets pull
  // from perBrandHealthVar, the primary target from dependentValue.
  const targetWeeks = useMemo(
    () =>
      weeks.map(w => ({
        ...w,
        dependentValue: w.perBrandHealthVar[stat.key] ?? w.dependentValue
      })),
    [weeks, stat.key]
  );
  const hasBpm = isPrimary && weeks.some(w => w.bpmDependentValue !== undefined);
  // Shaded call-outs on the chart for weeks where MMM unusually diverges from the
  // canonical value -- same fixed-threshold rule and shared computeDivergentRanges
  // already used on Portfolio/Channels (see divergence-ranges.ts).
  const divergentRanges = useMemo(
    () =>
      hasBpm
        ? computeDivergentRanges(
            targetWeeks.map(w => ({
              xValue: w.weekLabel,
              mmm: w.dependentValue,
              bpm: w.bpmDependentValue
            }))
          )
        : [],
    [targetWeeks, hasBpm]
  );
  const groundTruthWeeks = useMemo(
    () =>
      targetWeeks.map(w => ({
        weekIso: w.weekIso,
        mmmValue: w.dependentValue,
        bpmValue: w.bpmDependentValue
      })),
    [targetWeeks]
  );
  const formatStat = isCurrency ? money0.format : number2.format;
  const series: SeriesConfig[] = [
    {
      dataKey: 'dependentValue',
      label: hasBpm ? `MMM ${stat.label}` : stat.label,
      color: DivergentPositive400,
      format: isCurrency ? 'currency' : 'number',
      axis: 'left'
    }
  ];
  if (hasBpm) {
    series.push({
      dataKey: 'bpmDependentValue',
      // Just "Canonical", not "BPM canonical" -- the only source that's ever
      // populated this field so far is GQV's gqv_v2, not BPM.
      label: 'Canonical',
      color: CANONICAL_COLOR,
      format: isCurrency ? 'currency' : 'number',
      axis: 'left',
      dashed: true
    });
  }
  const tiles: KpiTileData[] = [
    { label: 'Avg Weekly', value: formatStat(stat.mean) },
    { label: 'Minimum Weekly', value: formatStat(stat.min) },
    { label: 'Maximum Weekly', value: formatStat(stat.max) }
  ];

  return (
    <div className='data-qa-dv-card'>
      <button
        type='button'
        className='data-qa-dv-card__head'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className='data-qa-dv-card__title'>{stat.label}</span>
        <span className='data-qa-dv-card__chevron'>
          {open ? <MdExpandLess /> : <MdExpandMore />}
        </span>
      </button>
      {open && (
        <div className='data-qa-dv-card__body'>
          <KpiBar tiles={tiles} />
          <DataQaChart
            title={`${stat.label} Over Time`}
            weeks={targetWeeks}
            series={series}
            bookmarkWeekIso={bookmarkWeekIso}
            isColdStart={isColdStart}
            height={320}
            hideLegend={!hasBpm}
            divergentRanges={divergentRanges}
          />
          {hasBpm && (
            <GqvGroundTruthPanel
              company={company}
              weeks={groundTruthWeeks}
              reviewGranularity={reviewGranularity}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface BrandIndexCardProps {
  metrics: { key: string; label: string }[];
  weeks: WeekRow[];
  bookmarkWeekIso?: string;
  isColdStart: boolean;
  // Redshift table slug -- only needed to build the BrandIndex compare-to-canonical SQL block.
  company: string;
  // dataset.reviewGranularity -- only needed to build the BrandIndex compare-to-canonical
  // SQL block's canonical-side bucketing (see buildBrandIndexGroundTruthSql's
  // dateTruncUnitFor).
  reviewGranularity?: string;
}

// Brand Equity's YouGov BrandIndex stage (config.brandHealthVars) -- combines all 4
// metrics (awareness/consideration/buzz/purchase intent) onto ONE chart rather than one
// card each, per Sean's call (2026-08-29): with only one config's own dependentValue to
// review at a time, the previous per-stat DepTargetCard loop rendered 4 nearly-identical
// collapsible cards; here there's a genuinely combined view instead. No approve/flag or
// KPI-tile bar of its own -- those stay at the section level above (unchanged), and a
// 4-metric x {Avg,Min,Max} tile grid would be busier than useful without per-metric
// grouping the rest of this page doesn't otherwise do (Channels' own single-approve
// consolidation set the precedent for skipping this kind of per-item repetition).
const BrandIndexCard = ({
  metrics,
  weeks,
  bookmarkWeekIso,
  isColdStart,
  company,
  reviewGranularity
}: BrandIndexCardProps) => {
  const hasCanonical = weeks.some(w => w.perBrandHealthVarCanonical);

  // DataQaChart's series read plain string dataKeys straight off each week object --
  // flatten each metric's (and its canonical counterpart's) value onto a top-level key
  // per week here, rather than teaching DataQaChart to resolve a nested accessor.
  const chartWeeks = useMemo(
    () =>
      weeks.map(w => {
        const values: Record<string, number> = {};
        for (const metric of metrics) {
          values[metric.key] = w.perBrandHealthVar[metric.key] ?? 0;
          if (w.perBrandHealthVarCanonical) {
            values[`${metric.key}__canonical`] = w.perBrandHealthVarCanonical[metric.key] ?? 0;
          }
        }
        return { ...w, ...values };
      }),
    [weeks, metrics]
  );

  const series: SeriesConfig[] = useMemo(
    () =>
      metrics.flatMap(metric => {
        const color = BRAND_INDEX_COLORS[metric.key] ?? DEFAULT_BRAND_INDEX_COLOR;
        const entries: SeriesConfig[] = [
          { dataKey: metric.key, label: metric.label, color, format: 'number', axis: 'left' }
        ];
        if (hasCanonical) {
          entries.push({
            dataKey: `${metric.key}__canonical`,
            label: `${metric.label} (Canonical)`,
            color,
            format: 'number',
            axis: 'left',
            dashed: true
          });
        }
        return entries;
      }),
    [metrics, hasCanonical]
  );

  // Shaded call-outs for weeks where any one metric's MMM value unusually diverges from
  // its own canonical value -- same fixed-threshold rule Portfolio/Channels/GQV already
  // use (divergence-ranges.ts), computed independently per metric then combined into one
  // set since all 4 metrics share this one chart. Re-sorted into real chronological order
  // after combining -- DataQaChart's divergence layer assumes ranges arrive left-to-right
  // for its label-collision avoidance, which flatMap-per-metric alone wouldn't guarantee
  // (metric B's early ranges would otherwise land after ALL of metric A's, not
  // interleaved by date). NOTE: the resulting highlight doesn't say which metric
  // diverged, only that one did -- worth revisiting if that turns out to matter in
  // practice with 4 metrics sharing one chart.
  const divergentRanges = useMemo(() => {
    if (!hasCanonical) return [];
    const weekIndex = new Map(chartWeeks.map((w, i) => [w.weekLabel, i]));
    const ranges = metrics.flatMap(metric =>
      computeDivergentRanges(
        chartWeeks.map(w => ({
          xValue: w.weekLabel,
          mmm: w.perBrandHealthVar[metric.key] ?? 0,
          bpm: w.perBrandHealthVarCanonical?.[metric.key]
        }))
      )
    );
    return [...ranges].sort((a, b) => (weekIndex.get(a.from) ?? 0) - (weekIndex.get(b.from) ?? 0));
  }, [metrics, chartWeeks, hasCanonical]);

  const groundTruthWeeks = useMemo(
    () =>
      weeks.map(w => ({
        weekIso: w.weekIso,
        mmmByMetric: w.perBrandHealthVar,
        canonicalByMetric: w.perBrandHealthVarCanonical
      })),
    [weeks]
  );

  return (
    <div className='data-qa-dv-card'>
      <div className='data-qa-dv-card__body'>
        <DataQaChart
          title='BrandIndex Metrics Over Time'
          weeks={chartWeeks}
          series={series}
          bookmarkWeekIso={bookmarkWeekIso}
          isColdStart={isColdStart}
          height={360}
          divergentRanges={divergentRanges}
        />
        {hasCanonical && (
          <BrandIndexGroundTruthPanel
            company={company}
            metrics={metrics}
            weeks={groundTruthWeeks}
            reviewGranularity={reviewGranularity}
          />
        )}
      </div>
    </div>
  );
};

export const DependentVarSection = ({
  config,
  dataset,
  diffs,
  status,
  notes,
  pending,
  isColdStart,
  onApprove,
  onFlag,
  onNoteChange,
  company,
  configs,
  approveBlockedReason
}: DependentVarSectionProps) => {
  const modelType = modelTypeForProductType(config.productType);
  // Bounds the chart x-axis to the model's own training window instead of plotting its
  // entire data history, same fix already applied to Portfolio/Channels.
  const chartWeeks = useMemo(
    () =>
      filterWeeksToFencepost(
        dataset.weeks,
        config.fencepostStart,
        config.fencepostEnd,
        dataset.reviewGranularity
      ),
    [dataset.weeks, config.fencepostStart, config.fencepostEnd, dataset.reviewGranularity]
  );
  // Brand Equity's GQV stage (dependent_var "gqv_combined", brandEquityStage 's1') is a
  // search-interest index, not a dollar-denominated metric -- every other product type,
  // and Brand Equity's own second stage (Total Business), is currency. This is a
  // structural fact of Brand Equity's fixed two-stage design (GQV vs. Total Business),
  // not a per-client choice, so it's safe to key off the stage identifier directly
  // rather than guessing from the dependent_var name.
  const isCurrency = config.brandEquityStage !== 's1';

  const geo = useMemo(() => dataset.dependentGeoBreakdown ?? [], [dataset.dependentGeoBreakdown]);
  // The backend's GeoDmaValue never sets dmaName (only dmaCode/value -- see
  // mmmDataQAGeoAdapter.ts) -- DmaChoropleth resolves real names from a CDN fetch for
  // its own map tooltips/stats, so this tile reuses that resolution via
  // onDmaNamesLoaded below instead of duplicating the fetch a third time.
  const [dmaNames, setDmaNames] = useState<Record<string, string>>({});
  const geoTiles = useMemo((): KpiTileData[] => {
    const withValue = geo.filter(d => d.value > 0);
    const total = withValue.reduce((sum, d) => sum + d.value, 0);
    const sorted = [...withValue].sort((a, b) => b.value - a.value);
    const top = sorted[0];
    const top3 = sorted.slice(0, 3).reduce((sum, d) => sum + d.value, 0);
    const top3Share = total > 0 ? (top3 / total) * 100 : 0;
    const balanced = top3Share <= 80;
    return [
      { label: 'DMAs with Sales', value: String(withValue.length) },
      {
        label: 'Top DMA',
        value: top ? (top.dmaName ?? dmaNames[top.dmaCode] ?? `DMA ${top.dmaCode}`) : '—'
      },
      {
        label: 'Top 3 Concentration',
        value: `${top3Share.toFixed(1)}%`,
        badge: { text: balanced ? 'Balanced' : 'Top-heavy', kind: balanced ? 'strong' : 'weak' }
      }
    ];
  }, [geo, dmaNames]);

  // Reviewed count comes from diffs.summary (this section's own real fingerprint diff),
  // not a per-week flag -- keeps this bar Dependent-Variable-specific rather than
  // reflecting changes anywhere else in the model. Gated on APPROVED specifically --
  // a flagged section isn't done (Sean, 2026-08-27): "the bar should reflect what's
  // been reviewed AND approved."
  const reviewedWeekCount = status === SectionStatus.APPROVED ? diffs.summary.unchanged : 0;
  const showGeo = config.productType === ProductType.GEO_MMM;
  // BrandIndex combines 4 metrics onto one chart -- "Target variable: yougov__awareness"
  // (this config's own single dependent_var) doesn't make sense once the tab represents
  // all 4 at once (Sean, 2026-08-29). Lower-cased, comma-joined labels ("awareness,
  // consideration, buzz, purchase intent"), not the raw yougov__<metric> column names.
  const targetVariableSubtitle = config.brandHealthVars
    ? `Target variables: ${config.brandHealthVars.map(m => m.label.toLowerCase()).join(', ')}`
    : `Target variable: ${config.dependentVar}`;

  const { models: exportModels, unavailable: exportUnavailable } = useAllModelsExport(
    company,
    configs,
    'Dependent variable export'
  );
  const setError = useSetError();
  const handleExportExcel = useCallback(async () => {
    try {
      const sheets = buildDependentVarExportSheets(company, exportModels);
      await exportMultiSheetToExcel(sheets, `${company}_dv_export`);
    } catch (e) {
      setError({
        title: "Couldn't export the dependent variable",
        message: `Dependent variable export failed: ${e.message}`,
        reportError: e
      });
    }
  }, [company, exportModels, setError]);

  return (
    <div className='data-qa-dv'>
      <SectionHeader
        title='Dependent Variable Review'
        modelType={modelType}
        subtitle={targetVariableSubtitle}
        tips={TIPS}
      />

      <div className='data-qa-dv__toolbar'>
        <DownloadDropdown
          size={Size.SM}
          disabled={exportUnavailable}
          menuOptions={['XLSX']}
          onClickOptions={[handleExportExcel]}
        />
      </div>

      {config.brandHealthVars ? (
        <BrandIndexCard
          metrics={config.brandHealthVars}
          weeks={chartWeeks}
          bookmarkWeekIso={diffs.bookmarkWeekIso}
          isColdStart={isColdStart}
          company={company}
          reviewGranularity={dataset.reviewGranularity}
        />
      ) : (
        dataset.dependentVarStats.map((stat, i) => (
          <Fragment key={stat.key}>
            <DepTargetCard
              stat={stat}
              weeks={chartWeeks}
              bookmarkWeekIso={diffs.bookmarkWeekIso}
              isColdStart={isColdStart}
              defaultOpen={i === 0}
              isPrimary={i === 0}
              isCurrency={isCurrency}
              company={company}
              reviewGranularity={dataset.reviewGranularity}
            />
          </Fragment>
        ))
      )}

      {showGeo && (
        <div className='data-qa-dv__geo'>
          <div className='data-qa-dv__geo-title'>Geographic Distribution</div>
          {geo.length > 0 ? (
            <>
              <KpiBar tiles={geoTiles} />
              <DmaChoropleth
                data={geo}
                formatValue={money0.format}
                showStats={false}
                onDmaNamesLoaded={setDmaNames}
              />
            </>
          ) : (
            <div className='data-qa-dv__geo-empty'>
              Geographic distribution unavailable — no per-DMA breakdown provided for this geoMMM
              config.
            </div>
          )}
        </div>
      )}
      <ReviewControls
        status={status}
        notes={notes}
        pending={pending}
        onApprove={onApprove}
        onFlag={onFlag}
        onNoteChange={onNoteChange}
        approveLabel='Approve Target Var'
        flagLabel='Flag Target Var'
        approveBlockedReason={approveBlockedReason}
        summary={diffs.summary}
        isColdStart={isColdStart}
        reviewed={reviewedWeekCount}
        total={dataset.weeks.length}
      />
    </div>
  );
};
